import {
  Box,
  Button,
  Checkbox,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Modal,
  ModalBody,
  Search,
  SegmentedControl,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { type Asset } from '@/services/db/asset/entity';

import { fileSize, SORT_OPTIONS } from '../utils';
import { setPlumaModalSize } from '../set-pluma-modal-size';
import { useAssetLibrary } from './use-asset-library';
import { useRefetchAssets } from './hooks/use-refetch-assets';
import { useDeleteAssetsAction } from './hooks/use-delete-assets';
import { useAssetOptimizerModal, usePromptOptimize } from '../optimizer';
import { AssetDropzone } from '../upload';
import { Assets } from './assets';
import { CreateFolderModal } from './components/create-folder-modal';
import { DeleteConfirmationModal } from './components/delete-confirmation-modal';
import * as styles from './components/index.css';
import { useAssetNavigation } from './hooks/use-asset-navigation';
import { useAssetPermissions } from './hooks/use-asset-permissions';
import { $sort, $viewMode, browserReducer, initialBrowserState, type SortField, type ViewMode } from './store';
import { canDeleteAsset } from './utils/asset-validation';

export function AssetLibraryModal() {
  const { onSelect, onClose, isOpen, options } = useAssetLibrary();
  const refetchAssets = useRefetchAssets();

  const filterFileSizeInBytes = options?.filterFileSizeInBytes;
  const filterPdfs = options?.filterPdfs;

  const [state, dispatch] = useReducer(browserReducer, initialBrowserState);

  const viewMode = useStore($viewMode);
  const sort = useStore($sort);

  // Reset local state when the modal opens
  const previousIsOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      dispatch({ type: 'RESET' });
    }
    previousIsOpen.current = isOpen;
  }, [isOpen]);

  const { isOpen: isAssetOptimizerOpen } = useAssetOptimizerModal();
  const [isConfirmOptimizationOpen] = usePromptOptimize();
  const permissions = useAssetPermissions();

  const setParents = useCallback((parents: Asset[]) => dispatch({ type: 'SET_PARENTS', parents }), []);
  const navigation = useAssetNavigation(state.parents, setParents);

  const sortLabel = useMemo(() => {
    return SORT_OPTIONS.find((o) => o.value === sort.field)?.label ?? '';
  }, [sort.field]);

  const handleOpenDeleteAssetModal = useCallback((asset: Asset) => {
    if (!canDeleteAsset(asset)) return;
    dispatch({ type: 'SET_ASSETS_TO_DELETE', assets: [asset] });
  }, []);

  const handleSetSortBy = useCallback((field: SortField) => {
    $sort.set({ ...$sort.get(), field });
    dispatch({ type: 'SET_PAGE_NUMBER', page: 1 });
  }, []);

  const handleToggleSortDirection = useCallback(() => {
    const current = $sort.get();
    $sort.set({ ...current, ascending: current.ascending !== true });
    dispatch({ type: 'SET_PAGE_NUMBER', page: 1 });
  }, []);

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    $viewMode.set(mode);
  }, []);

  const handleSetSearchInput = useCallback((input: string) => {
    dispatch({ type: 'SET_SEARCH_INPUT', input });
  }, []);

  const handleSetSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', query });
  }, []);

  const handleToggleAssetSelection = useCallback((checked: boolean, assetId: string) => {
    dispatch({ type: 'TOGGLE_ASSET_SELECTION', checked, assetId });
  }, []);

  const handleClearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  const handleSelectAll = useCallback((assets: Asset[]) => {
    dispatch({ type: 'SELECT_ALL', assets });
  }, []);

  const handleSetAssetsToDelete = useCallback((assets: Asset[]) => {
    dispatch({ type: 'SET_ASSETS_TO_DELETE', assets });
  }, []);

  const handleSetPageNumber = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE_NUMBER', page });
  }, []);

  const navigationProps = useMemo(
    () => ({
      parentFolderId: navigation.parentFolderId,
      folderPaths: navigation.folderPaths,
      navigateToFolder: navigation.navigateToFolder,
      onParentsChange: setParents,
    }),
    [navigation.parentFolderId, navigation.folderPaths, navigation.navigateToFolder, setParents],
  );

  const selectionProps = useMemo(
    () => ({
      checkedAssetIds: state.selectedAssetIds,
      onCheckAsset: handleToggleAssetSelection,
      onClearCheckedAssets: handleClearSelection,
      onCheckAllAssets: handleSelectAll,
      onDeleteAsset: handleOpenDeleteAssetModal,
      onDeleteCheckedAssets: handleSetAssetsToDelete,
    }),
    [
      state.selectedAssetIds,
      handleOpenDeleteAssetModal,
      handleToggleAssetSelection,
      handleClearSelection,
      handleSelectAll,
      handleSetAssetsToDelete,
    ],
  );

  const filtersProps = useMemo(
    () => ({
      search: state.search,
      setSearchInput: handleSetSearchInput,
      setSearchQuery: handleSetSearchQuery,
      filterFileSizeInBytes,
      filterPdfs,
      isFilteringByBytesEnabled: state.isFilteringByBytesEnabled,
    }),
    [
      state.search,
      filterFileSizeInBytes,
      filterPdfs,
      state.isFilteringByBytesEnabled,
      handleSetSearchInput,
      handleSetSearchQuery,
    ],
  );

  const viewProps = useMemo(
    () => ({
      viewMode,
      sortBy: sort.field,
      sortAsc: sort.ascending,
      pageNumber: state.pageNumber,
      setPageNumber: handleSetPageNumber,
    }),
    [viewMode, sort.field, sort.ascending, state.pageNumber, handleSetPageNumber],
  );

  const handleDeletedAssets = useCallback((deletedAssets: Asset[]) => {
    for (const asset of deletedAssets) {
      dispatch({ type: 'TOGGLE_ASSET_SELECTION', checked: false, assetId: asset.id });
    }
    dispatch({ type: 'SET_ASSETS_TO_DELETE', assets: [] });
  }, []);

  const confirmDeleteAssets = useDeleteAssetsAction({ onDeleted: handleDeletedAssets });

  const handleConfirmDeleteAssets = useCallback(async () => {
    await confirmDeleteAssets(state.activeModal.assetsToDelete);
  }, [confirmDeleteAssets, state.activeModal.assetsToDelete]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (isAssetOptimizerOpen || isConfirmOptimizationOpen || state.activeModal.type !== null) {
            return;
          }
          onClose();
        }}
        title="Select Asset"
        shouldShowCloseButton={true}
        style={setPlumaModalSize({ maxWidth: '1200px' })}
      >
        <AssetDropzone
          allowedFileCategories={['image']}
          parentFolderId={navigation.parentFolderId}
          parentFolderName={navigation.currentFolderName}
          disabled={!permissions.canCreate}
        >
          {({ open, getRootProps, children }) => (
            <ModalBody {...getRootProps()} position="relative">
              {children}
              <Divider />
              <Box display="flex" justifyContent="space-between" my="150">
                <Search
                  ariaLabel="Search assets"
                  placeholder="Search"
                  value={state.search.input}
                  onSearchInput={handleSetSearchInput}
                  onSearch={handleSetSearchQuery}
                  shouldSearchOnDebounce={500}
                  shouldSearchOnClear={true}
                  shouldSearchOnEnter={true}
                />
                <Box display="flex" gap="100" alignItems="center">
                  <DropdownMenu>
                    <DropdownMenuTriggerButton
                      variant="tertiary"
                      icon="chevron-down"
                      iconPosition="trailing"
                      aria-label="Sort by"
                      size="sm"
                    >
                      {sortLabel}
                    </DropdownMenuTriggerButton>
                    <DropdownMenuContent>
                      <DropdownMenuGroup label="Sort assets by">
                        {SORT_OPTIONS.map((option) => (
                          <DropdownMenuItem key={option.value} onClick={() => handleSetSortBy(option.value)}>
                            {option.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="tertiary"
                    icon={sort.ascending === true ? 'sort-ascending' : 'sort-descending'}
                    isIconOnly
                    aria-label="Sort direction"
                    onClick={handleToggleSortDirection}
                    size="sm"
                  />
                  <SegmentedControl
                    ariaLabel="View mode"
                    name="asset-library-view-mode"
                    value={viewMode}
                    onChange={(value) => handleSetViewMode(value as ViewMode)}
                    options={[
                      { value: 'grid', ariaLabel: 'Grid view', icon: 'grid' },
                      { value: 'list', ariaLabel: 'List view', icon: 'list-bullet' },
                    ]}
                    size="sm"
                  />
                  <Tooltip
                    content="Your user role does not have access to this feature."
                    isDisabled={permissions.canCreate}
                    placement="bottom"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        onClick={() => dispatch({ type: 'SHOW_CREATE_FOLDER_MODAL', show: true })}
                        variant="tertiary"
                        size="sm"
                        icon="folder-new"
                        isDisabled={!permissions.canCreate}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      >
                        New Folder
                      </Button>
                    )}
                  </Tooltip>
                  <Tooltip
                    content="Your user role does not have access to this feature."
                    isDisabled={permissions.canCreate}
                    placement="bottom"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        onClick={open}
                        variant="primary"
                        size="sm"
                        isDisabled={!permissions.canCreate}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      >
                        Upload file
                      </Button>
                    )}
                  </Tooltip>
                </Box>
              </Box>
              <Divider />
              {filterFileSizeInBytes != null ? (
                <Box mt="050">
                  <Checkbox
                    name="bytes_limit"
                    label={`Only show assets that meet the selected limit of ${fileSize(filterFileSizeInBytes)}.`}
                    isChecked={state.isFilteringByBytesEnabled}
                    onCheckedChange={() =>
                      dispatch({ type: 'SET_FILTERING_BY_BYTES', enabled: !state.isFilteringByBytesEnabled })
                    }
                  />
                </Box>
              ) : null}
              <Box display="flex" flexDirection="column" flexGrow="1" className={styles.assetsContainer}>
                <Assets
                  enabled={isOpen}
                  navigation={navigationProps}
                  selection={selectionProps}
                  filters={filtersProps}
                  view={viewProps}
                  onSelect={onSelect}
                />
              </Box>
            </ModalBody>
          )}
        </AssetDropzone>
      </Modal>

      <CreateFolderModal
        isOpen={state.activeModal.type === 'createFolder'}
        onClose={() => dispatch({ type: 'SHOW_CREATE_FOLDER_MODAL', show: false })}
        onSuccess={() => {
          dispatch({ type: 'SHOW_CREATE_FOLDER_MODAL', show: false });
          refetchAssets();
        }}
        parentFolderId={navigation.parentFolderId}
      />

      <DeleteConfirmationModal
        assetsToDelete={state.activeModal.assetsToDelete}
        onClose={() => dispatch({ type: 'SET_ASSETS_TO_DELETE', assets: [] })}
        onConfirm={handleConfirmDeleteAssets}
      />
    </>
  );
}
